/**
 * AdminStarAssist — issue/revoke API keys, browse conversations, quick stats.
 * KB editing is intentionally minimal (title + keywords + answer + active) —
 * the founder can grow the FAQ from here without touching Mongo directly.
 */
import { useEffect, useMemo, useState, Fragment as FragmentWithKey } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Trash2, Plus, KeyRound, MessageSquare, TrendingUp, ShieldOff, Search, Filter, Edit3, Save, X, Play, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";

export default function AdminStarAssist() {
  const [tab, setTab] = useState("keys");
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/agent/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// HTP Assist</div>
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tighter mt-1">HTP Assist AI · Control</h1>
        <p className="text-sm text-white/60 mt-1">
          The in-house AI agent that replies on the site bubble, member portal, and (via API key) your WhatsApp Business.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Conversations" value={stats.total || 0} />
        <Stat label="Answer rate" value={`${stats.answer_rate || 0}%`} accent="green" />
        <Stat label="Escalated to human" value={stats.escalated || 0} accent="orange" />
        <Stat label="From WhatsApp" value={stats.from_whatsapp || 0} />
      </div>

      <div className="mb-4 border-b border-white/10 flex gap-2">
        {[["keys","API Keys",KeyRound],["kb","Knowledge Base",MessageSquare],["convos","Conversations",TrendingUp]].map(([v,l,I]) => (
          <button key={v} data-testid={`sa-tab-${v}`} onClick={() => setTab(v)}
            className={`px-3 py-2 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 border-b-2 ${tab===v?"border-brand-orange text-white":"border-transparent text-white/50 hover:text-white"}`}>
            <I size={12}/> {l}
          </button>
        ))}
      </div>

      {tab === "keys"   && <KeysPanel />}
      {tab === "kb"     && <KbPanel />}
      {tab === "convos" && <ConvosPanel />}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const tone = accent === "green" ? "text-green-300" : accent === "orange" ? "brand-orange" : "brand-blue";
  return (
    <div className="border border-white/12 bg-[#131A2B] p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-display text-3xl font-black mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function KeysPanel() {
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("SaaSyto WhatsApp");
  const [newKey, setNewKey] = useState("");
  const load = () => api.get("/admin/agent/keys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const issue = async () => {
    try {
      const r = await api.post("/admin/agent/keys", { label });
      setNewKey(r.data.key);
      load();
      toast.success("Key issued — copy it now, you won't see it again");
    } catch (e) { toast.error(apiError(e)); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this key permanently?")) return;
    await api.post(`/admin/agent/keys/${id}/revoke`); load(); toast.success("Revoked");
  };
  return (
    <div className="space-y-4">
      <div className="border border-brand-orange/40 bg-brand-orange/5 p-4">
        <div className="text-xs text-white/70 mb-2">Paste one of these keys into your WhatsApp provider&apos;s webhook config so incoming messages auto-reply through HTP Assist.</div>
        <div className="flex gap-2">
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Key label (e.g. SaaSyto webhook)"
            data-testid="sa-key-label" className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          <button onClick={issue} data-testid="sa-key-issue"
            className="bg-brand-orange text-black px-3 py-2 font-bold text-xs uppercase tracking-widest hover:bg-white">
            <Plus size={14} className="inline"/> Issue Key
          </button>
        </div>
        {newKey && (
          <div className="mt-3 border border-green-500/50 bg-green-500/10 p-3 flex items-center gap-3 font-mono text-xs" data-testid="sa-key-new">
            <code className="flex-1 break-all">{newKey}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey); toast.success("Copied"); }}
              className="border border-green-500/50 text-green-300 hover:bg-green-500/20 px-2 py-1">
              <Copy size={12}/>
            </button>
            <button onClick={() => setNewKey("")} className="text-white/50 hover:text-white text-xs">Hide</button>
          </div>
        )}
      </div>

      <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/5">
            <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
              <th className="p-2">Label</th><th className="p-2">Key</th><th className="p-2">Calls</th><th className="p-2">Last used</th><th className="p-2">Status</th><th className="p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && <tr><td colSpan={6} className="text-center p-6 text-white/40">No keys yet. Issue one above.</td></tr>}
            {keys.map((k) => (
              <tr key={k.id} className="border-t border-white/8">
                <td className="p-2">{k.label}</td>
                <td className="p-2 font-mono">{k.masked}</td>
                <td className="p-2">{k.call_count || 0}</td>
                <td className="p-2 text-white/50">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("en-IN") : "—"}</td>
                <td className="p-2">
                  {k.revoked ? <span className="text-red-300 text-[10px] font-mono uppercase">Revoked</span>
                             : <span className="text-green-300 text-[10px] font-mono uppercase">Active</span>}
                </td>
                <td className="p-2 text-right">
                  {!k.revoked && (
                    <button onClick={() => revoke(k.id)} data-testid={`sa-key-revoke-${k.id}`}
                      className="text-red-300 border border-red-500/40 hover:bg-red-500/20 px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                      <ShieldOff size={10}/> Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KbPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const empty = { title: "", keywords: "", answer: "", category: "general", followups: "", active: true };
  const [draft, setDraft] = useState(empty);
  const [test, setTest] = useState({ query: "", result: null, busy: false });

  const load = () => api.get("/admin/agent/kb").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const cats = new Set(rows.map((r) => r.category || "general"));
    return ["all", ...Array.from(cats).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const query = q.toLowerCase().trim();
    return rows.filter((r) => {
      if (catFilter !== "all" && (r.category || "general") !== catFilter) return false;
      if (!query) return true;
      const hay = [r.title, r.answer, (r.keywords || []).join(" "), r.category].join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [rows, q, catFilter]);

  const toPayload = (d) => ({
    ...d,
    keywords: (d.keywords || "").split(",").map((s) => s.trim()).filter(Boolean),
    followups: (d.followups || "").split(",").map((s) => s.trim()).filter(Boolean),
    active: !!d.active,
  });

  const save = async () => {
    if (!draft.title.trim() || !draft.answer.trim()) { toast.error("Title and answer required"); return; }
    try {
      await api.post("/admin/agent/kb", toPayload(draft));
      load();
      setDraft(empty);
      setShowNew(false);
      toast.success("KB entry added — HTP Assist will use it immediately.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      title: row.title || "",
      keywords: (row.keywords || []).join(", "),
      answer: row.answer || "",
      category: row.category || "general",
      followups: (row.followups || []).join(", "),
      active: row.active !== false,
    });
  };

  const saveEdit = async () => {
    if (!draft.title.trim() || !draft.answer.trim()) { toast.error("Title and answer required"); return; }
    try {
      await api.put(`/admin/agent/kb/${editingId}`, toPayload(draft));
      load();
      setEditingId(null);
      setDraft(empty);
      toast.success("Saved — the bot will use the new copy immediately.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/admin/agent/kb/${row.id}`, {
        title: row.title, answer: row.answer, category: row.category || "general",
        keywords: row.keywords || [], followups: row.followups || [],
        active: !(row.active !== false),
      });
      load();
      toast.success(row.active !== false ? "Disabled" : "Enabled");
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this KB entry? This cannot be undone.")) return;
    await api.delete(`/admin/agent/kb/${id}`);
    load();
    toast.success("Deleted");
  };

  const runTest = async () => {
    if (!test.query.trim()) return;
    setTest((s) => ({ ...s, busy: true, result: null }));
    try {
      const { data } = await api.post("/agent/ask", { message: test.query, session_id: "admin-test" });
      setTest((s) => ({ ...s, busy: false, result: data }));
    } catch (e) {
      setTest((s) => ({ ...s, busy: false, result: { answer: apiError(e), escalate: true, confidence: 0 } }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Test box — try a live query against the KB */}
      <div className="border border-brand-blue/40 bg-brand-blue/5 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Play size={14} className="text-brand-blue" />
          <div className="font-mono text-[11px] uppercase tracking-widest text-brand-blue">Try a question · see which entry answers</div>
        </div>
        <div className="flex gap-2">
          <input
            value={test.query}
            onChange={(e) => setTest((s) => ({ ...s, query: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && runTest()}
            placeholder="e.g. what are academy fees?"
            data-testid="kb-test-query"
            className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm"
          />
          <button onClick={runTest} disabled={test.busy}
            data-testid="kb-test-run"
            className="bg-brand-blue text-white px-4 py-2 font-bold text-xs uppercase tracking-widest hover:bg-blue-500 disabled:opacity-40">
            {test.busy ? "…" : "Test"}
          </button>
        </div>
        {test.result && (
          <div className="mt-3 border border-white/10 bg-black/40 p-3 text-sm" data-testid="kb-test-result">
            <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
              <span>Source: <span className="text-white">{test.result.kind}</span></span>
              <span>Confidence: <span className={test.result.confidence >= 65 ? "text-green-400" : "text-red-400"}>{Math.round(test.result.confidence)}%</span></span>
              {test.result.escalate && <span className="text-orange-300">→ Escalated to human</span>}
            </div>
            <div className="whitespace-pre-line text-white/90">{test.result.answer}</div>
          </div>
        )}
      </div>

      {/* Search + filter + new button */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, keywords, answer…"
            data-testid="kb-search"
            className="w-full bg-black/40 border border-white/15 pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <div className="relative">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            data-testid="kb-cat-filter"
            className="bg-black/40 border border-white/15 pl-9 pr-8 py-2 text-sm capitalize appearance-none"
          >
            {categories.map((c) => <option key={c} value={c}>{c === "all" ? `All (${rows.length})` : c}</option>)}
          </select>
        </div>
        <button
          onClick={() => { setShowNew((v) => !v); setDraft(empty); setEditingId(null); }}
          data-testid="kb-new-toggle"
          className={`px-3 py-2 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5 ${showNew ? "bg-white/10 text-white" : "bg-brand-orange text-black hover:bg-white"}`}
        >
          {showNew ? <><X size={12}/> Cancel</> : <><Plus size={14}/> New Entry</>}
        </button>
      </div>

      {/* New entry form */}
      {showNew && (
        <EntryForm draft={draft} setDraft={setDraft} onSave={save} saveLabel="Add to KB" />
      )}

      {/* Rows */}
      <div className="border border-white/12 bg-[#131A2B] overflow-hidden">
        <div className="max-h-[640px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5 sticky top-0 z-10">
              <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
                <th className="p-2">Title</th>
                <th className="p-2">Category</th>
                <th className="p-2 hidden md:table-cell">Keywords</th>
                <th className="p-2">Status</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center p-8 text-white/40">
                  {rows.length === 0 ? "No KB entries yet — add your first one above." : "No matches. Try a different search or category."}
                </td></tr>
              )}
              {filtered.map((r) => (
                <FragmentWithKey key={r.id}>
                  <tr className={`border-t border-white/8 ${editingId === r.id ? "bg-brand-orange/5" : ""} ${r.active === false ? "opacity-50" : ""}`}>
                    <td className="p-2 max-w-[260px]">
                      <div className="font-semibold truncate" title={r.title}>{r.title}</div>
                      <div className="text-white/50 text-[10px] mt-0.5 line-clamp-2" title={r.answer}>{r.answer}</div>
                    </td>
                    <td className="p-2 text-white/60 capitalize">{r.category || "general"}</td>
                    <td className="p-2 text-white/50 hidden md:table-cell max-w-[260px] truncate" title={(r.keywords || []).join(", ")}>
                      {(r.keywords || []).slice(0, 5).join(", ")}{(r.keywords || []).length > 5 ? ` +${r.keywords.length - 5}` : ""}
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => toggleActive(r)}
                        data-testid={`kb-toggle-${r.id}`}
                        title={r.active === false ? "Enable" : "Disable"}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${r.active === false ? "border border-red-500/40 text-red-300" : "border border-green-500/40 text-green-300"}`}
                      >
                        {r.active === false ? <EyeOff size={10}/> : <Eye size={10}/>}
                        {r.active === false ? "Off" : "Live"}
                      </button>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <button onClick={() => (editingId === r.id ? setEditingId(null) : startEdit(r))}
                        data-testid={`kb-edit-${r.id}`}
                        className="text-brand-blue hover:text-blue-300 mr-2">
                        {editingId === r.id ? <ChevronUp size={12} className="inline"/> : <Edit3 size={12}/>}
                      </button>
                      <button onClick={() => del(r.id)}
                        data-testid={`kb-del-${r.id}`}
                        className="text-red-300 hover:text-red-100">
                        <Trash2 size={12}/>
                      </button>
                    </td>
                  </tr>
                  {editingId === r.id && (
                    <tr>
                      <td colSpan={5} className="p-3 bg-black/40 border-t border-brand-orange/30">
                        <EntryForm draft={draft} setDraft={setDraft} onSave={saveEdit} saveLabel="Save changes" onCancel={() => { setEditingId(null); setDraft(empty); }} />
                      </td>
                    </tr>
                  )}
                </FragmentWithKey>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-2 border-t border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/40">
          Showing {filtered.length} of {rows.length} entries · {rows.filter((r) => r.active !== false).length} live · {rows.filter((r) => r.active === false).length} disabled
        </div>
      </div>
    </div>
  );
}

function EntryForm({ draft, setDraft, onSave, saveLabel, onCancel }) {
  return (
    <div className="border border-white/12 bg-[#131A2B] p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
      <input placeholder="Title (e.g. Refund policy)" value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        className="bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="kb-form-title" />
      <input placeholder="Category (payments / booking / …)" value={draft.category}
        onChange={(e) => setDraft({ ...draft, category: e.target.value.toLowerCase() })}
        className="bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="kb-form-category" />
      <input placeholder="Keywords, comma separated (cancel, refund, cancel booking …)" value={draft.keywords}
        onChange={(e) => setDraft({ ...draft, keywords: e.target.value })}
        className="md:col-span-2 bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="kb-form-keywords" />
      <textarea placeholder="Answer the bot will send. Use \n for line breaks. Emojis welcome." value={draft.answer}
        onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
        rows={4} className="md:col-span-2 bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="kb-form-answer" />
      <input placeholder="Follow-up chips (comma separated, optional)" value={draft.followups}
        onChange={(e) => setDraft({ ...draft, followups: e.target.value })}
        className="md:col-span-2 bg-black/40 border border-white/15 px-3 py-2 text-sm" data-testid="kb-form-followups" />
      <label className="md:col-span-2 flex items-center gap-2 text-xs text-white/70 cursor-pointer">
        <input type="checkbox" checked={!!draft.active}
          onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
          data-testid="kb-form-active" />
        Live (bot will use this entry). Uncheck to save as draft / disable.
      </label>
      <div className="md:col-span-2 flex gap-2">
        <button onClick={onSave} data-testid="kb-form-save"
          className="flex-1 bg-brand-orange text-black py-2 font-bold text-xs uppercase tracking-widest hover:bg-white inline-flex items-center justify-center gap-1.5">
          <Save size={12}/> {saveLabel}
        </button>
        {onCancel && (
          <button onClick={onCancel}
            className="border border-white/20 hover:bg-white/10 text-white py-2 px-4 font-bold text-xs uppercase tracking-widest">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function ConvosPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/agent/conversations").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="border border-white/12 bg-[#131A2B] overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-white/5"><tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
          <th className="p-2">When</th><th className="p-2">Question</th><th className="p-2">Answer</th><th className="p-2">Source</th><th className="p-2">Confidence</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-white/8">
              <td className="p-2 text-white/50 font-mono">{new Date(r.created_at).toLocaleString("en-IN")}</td>
              <td className="p-2 truncate max-w-xs">{r.question}</td>
              <td className="p-2 text-white/70 truncate max-w-md">{r.answer}</td>
              <td className="p-2 font-mono">{r.source}</td>
              <td className="p-2 font-mono">{Math.round(r.confidence)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
